/**
 * Retry a function with exponential backoff
 * @param {Function} fn - The async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {Function} options.shouldRetry - Function to determine if error should be retried
 * @returns {Promise} - Result of the function
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = (error) => {
      // Retry on network errors, 429 (rate limit), 500-599 (server errors)
      if (error.status === 429 || (error.status >= 500 && error.status < 600)) {
        return true;
      }
      // Retry on network errors
      if (error.message && (
        error.message.includes('network') ||
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch')
      )) {
        return true;
      }
      return false;
    }
  } = options;

  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry if we've exhausted retries or error shouldn't be retried
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, maxDelay);
    }
  }

  throw lastError;
}

/**
 * Validate AI response structure
 * @param {*} response - The response to validate
 * @param {string} expectedType - Expected response type ('message', 'table', 'feedback')
 * @returns {Object} - { valid: boolean, error?: string, data?: any }
 */
export function validateAIResponse(response, expectedType = 'message') {
  if (!response) {
    return { valid: false, error: 'Empty response received' };
  }

  switch (expectedType) {
    case 'message':
      if (typeof response === 'string' && response.trim().length > 0) {
        return { valid: true, data: response };
      }
      if (response.message && typeof response.message === 'string' && response.message.trim().length > 0) {
        return { valid: true, data: response.message };
      }
      return { valid: false, error: 'Invalid message response format' };

    case 'table':
      if (typeof response === 'object' && response !== null) {
        if (Array.isArray(response.rows) && Array.isArray(response.headings)) {
          return { valid: true, data: response };
        }
        if (response.autoFill && typeof response.autoFill === 'object') {
          return { valid: true, data: response.autoFill };
        }
      }
      return { valid: false, error: 'Invalid table response format' };

    case 'feedback':
      if (typeof response === 'string' && response.trim().length > 0) {
        return { valid: true, data: response };
      }
      if (response.feedback && typeof response.feedback === 'string' && response.feedback.trim().length > 0) {
        return { valid: true, data: response.feedback };
      }
      return { valid: false, error: 'Invalid feedback response format' };

    default:
      return { valid: true, data: response };
  }
}

/**
 * Get user-friendly error message from error object
 * @param {Error|Object} error - The error object
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error) {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // OpenAI-backed errors: show server copy + support reference (avoid masking with generic 429 text)
  if (error.code === 'INSUFFICIENT_QUOTA') {
    const base =
      error.message && !String(error.message).startsWith('HTTP')
        ? error.message
        : 'Please try again later. Contact support if this continues.';
    return error.requestId ? `${base} Reference ID: ${error.requestId}` : base;
  }

  // Handle error objects with status codes
  if (error.status) {
    switch (error.status) {
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 429: {
        const ref = error.requestId ? ` Reference ID: ${error.requestId}` : '';
        if (typeof error.retryAfter === 'number' && error.retryAfter > 0) {
          if (error.retryAfter >= 60) {
            const minutes = Math.ceil(error.retryAfter / 60);
            return `Too many requests. Please wait about ${minutes} minute${minutes > 1 ? 's' : ''} and try again.${ref}`;
          }
          return `Too many requests. Please wait about ${error.retryAfter} second${error.retryAfter > 1 ? 's' : ''} and try again.${ref}`;
        }
        return 'Too many requests. Please wait a few minutes and try again.';
      }
      case 500:
      case 502:
      case 503:
        return 'The AI service is temporarily unavailable. Please try again in a moment.';
      case 504:
        return 'The request took too long. Please try again.';
      default:
        return `Server error (${error.status}). Please try again.`;
    }
  }

  // Handle error messages
  if (error.message) {
    const msg = error.message.toLowerCase();
    
    if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch')) {
      return 'Network error. Please check your connection and try again.';
    }
    if (msg.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    if (msg.includes('rate limit')) {
      return 'Too many requests. Please wait up to 15 minutes and try again.';
    }
    if (msg.includes('invalid') || msg.includes('parse')) {
      return 'Invalid response received. Please try again.';
    }
    
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Get fallback response based on context
 * @param {string} context - The context ('chat', 'feedback', 'autofill')
 * @returns {string} - Fallback message
 */
export function getFallbackResponse(context = 'chat') {
  const fallbacks = {
    chat: "I'm having trouble connecting right now. Please try again in a moment, or refresh the page.",
    feedback: "We're having trouble generating feedback at the moment. Please try again later, or contact support if the issue persists.",
    autofill: "Auto-fill is temporarily unavailable. You can still fill in the table manually.",
    summary: "Unable to generate summary at this time. Please try again later."
  };

  return fallbacks[context] || fallbacks.chat;
}
